import { useMemo, useState } from 'react';
import { Activity, Clock3, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { InfoHint } from '../InfoHint';
import type { RelationshipIntelligenceModel, RelationshipInterventionWindow } from '../../lib/relationship-intelligence-engine';

type TrendScenario = 'steady' | 'fragile' | 'repair_run';

const SCENARIO_LABEL: Record<TrendScenario, string> = {
  steady: 'Hold steady',
  fragile: 'Fragile day',
  repair_run: 'Repair sprint',
};

function projectWindowShift(window: RelationshipInterventionWindow, scenario: TrendScenario) {
  if (scenario === 'fragile') {
    return {
      startsInHours: Math.max(1, window.startsInHours - 3),
      confidence: Math.max(0.35, window.confidence - 0.1),
    };
  }
  if (scenario === 'repair_run') {
    return {
      startsInHours: Math.max(1, window.startsInHours - 1),
      confidence: Math.min(0.96, window.confidence + 0.08),
    };
  }
  return {
    startsInHours: window.startsInHours,
    confidence: window.confidence,
  };
}

export function TrendTimingRailCard({
  model,
  onTrack,
}: {
  model: RelationshipIntelligenceModel;
  onTrack?: (event: string, properties?: Record<string, unknown>) => void;
}) {
  const [scenario, setScenario] = useState<TrendScenario>('steady');
  const scenarioWindows = useMemo(
    () =>
      model.interventionWindows.map((w) => {
        const shifted = projectWindowShift(w, scenario);
        return { ...w, startsInHours: shifted.startsInHours, confidence: shifted.confidence };
      }),
    [model.interventionWindows, scenario],
  );

  const trendSignal = model.direction === 'improving' ? 'Improving' : model.direction === 'worsening' ? 'Elevating' : 'Steady';
  const TrendIcon = model.direction === 'improving' ? TrendingDown : model.direction === 'worsening' ? TrendingUp : Activity;

  return (
    <Card className="p-5 border-indigo-200/80 bg-gradient-to-br from-indigo-50/90 via-violet-50/80 to-white dark:from-indigo-950/40 dark:via-violet-950/30 dark:to-neutral-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
            <span className="inline-flex items-center gap-1.5">
              Trend + timing simulator
              <InfoHint
                title="Trend + timing simulator"
                description="Try different day conditions to see how timing windows and confidence may shift before you decide what to do next."
              />
            </span>
          </p>
          <h2 className="mt-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">Relationship Health Snapshot</h2>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">{model.summary}</p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-indigo-300/70 bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:border-indigo-700 dark:bg-neutral-900 dark:text-indigo-300">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {model.weatherBand}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-xl border border-neutral-200 bg-white/85 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/80">
          <p className="text-[11px] uppercase text-neutral-500">Trend</p>
          <p className="text-sm font-semibold capitalize text-neutral-900 dark:text-neutral-100">{trendSignal}</p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white/85 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/80">
          <p className="text-[11px] uppercase text-neutral-500">Volatility</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{model.volatility.toFixed(1)}</p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white/85 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/80">
          <p className="text-[11px] uppercase text-neutral-500">Data strength</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{model.dataQuality}%</p>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white/85 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900/80">
          <p className="text-[11px] uppercase text-neutral-500">Samples</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{model.sampleCount}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {(['steady', 'fragile', 'repair_run'] as TrendScenario[]).map((next) => (
          <Button
            key={next}
            type="button"
            size="sm"
            variant={scenario === next ? 'default' : 'outline'}
            onClick={() => {
              setScenario(next);
              onTrack?.('ri_trend_scenario_selected', { scenario: next });
            }}
          >
            {SCENARIO_LABEL[next]}
          </Button>
        ))}
      </div>

      <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
        <span className="inline-flex items-center gap-1.5">
          Window + confidence guide
          <InfoHint
            title="How to read windows and confidence"
            description="Windows are suggested timing ranges for your next check-in or repair action. 'Starts in 6h' means the model expects that to be your best opening window. Confidence is how reliable that prediction is with your current data and volatility: higher confidence means timing guidance is more stable, lower confidence means use shorter feedback loops and reassess after your next check-in."
          />
        </span>
      </p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {scenarioWindows.slice(0, 2).map((w) => (
          <div key={`${w.label}-${scenario}`} className="rounded-xl border border-violet-200 bg-violet-50/70 px-3 py-2 dark:border-violet-800 dark:bg-violet-950/30">
            <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">{w.label}</p>
            <p className="mt-0.5 text-sm text-neutral-800 dark:text-neutral-100">
              Starts in <span className="font-semibold">{w.startsInHours}h</span> · Confidence {Math.round(w.confidence * 100)}%
            </p>
            <p className="mt-0.5 text-xs text-neutral-600 dark:text-neutral-300">{w.reason}</p>
          </div>
        ))}
      </div>

      <p className="mt-3 inline-flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-300">
        <TrendIcon className="h-3.5 w-3.5" />
        Scenario: {SCENARIO_LABEL[scenario]} · timing auto-adjusted for this simulation path.
      </p>
    </Card>
  );
}
